import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { ChatManager } from '../../../services/ChatManager/ChatManager';
import {
  setChatConnecting,
  setChatConnected,
  setChatDisconnected,
  setChatError,
  setUnreadCount,
} from '../../../redux/chat/chatSlice';
import { SessionManager } from '../../../services/SessionManager/SessionManager';
import { BuyerAPIHandler } from '../../../services/NetworkClient/BackendAPIs';
import { ErrorLogger } from '../../../utils/Logger';

/**
 * ChatInitializer - Centralized component for managing chat connection lifecycle
 * 
 * This component should be rendered once at the app level to ensure:
 * 1. Chat connection is established only once
 * 2. Event listeners are registered only once (directly dispatch to Redux)
 * 3. Unread count is synced with Redux store
 * 
 * Place this component in App.tsx or a similar top-level component.
 * 
 * Uses Redux auth state to reactively initialize/disconnect chat on login/logout.
 * ChatManager now directly dispatches unread count updates to Redux (no callbacks needed).
 */
export const ChatInitializer = () => {
  const dispatch = useDispatch();
  const initializationInFlight = useRef(false);
  const { isConnected, isConnecting, error } = useSelector((state: RootState) => state.chat);
  const { isLoggedIn, buyer } = useSelector((state: RootState) => state.auth);

  // Initialize chat when user logs in
  useEffect(() => {
    // Only initialize if user is logged in and not already connected/connecting
    if (!isLoggedIn || isConnected || isConnecting || error || initializationInFlight.current) {
      return;
    }

    const initializeChat = async () => {
      try {
        initializationInFlight.current = true;
        dispatch(setChatConnecting());

        // Check if already connected (another component might have initialized)
        if (ChatManager.shared.isConnected()) {
          dispatch(setChatConnected());
          return;
        }

        // Connect to chat using buyer from Redux or fetch if not available
        let buyerData = buyer || SessionManager.shared.buyer || await BuyerAPIHandler.shared.getBuyerByAuthToken();
        await ChatManager.shared.makeUserConnection(buyerData);

        dispatch(setChatConnected());
      } catch (error) {
        ErrorLogger.log(error, 'ChatInitializer.initializeChat');
        dispatch(setChatError(error instanceof Error ? error.message : 'Failed to connect to chat'));
      } finally {
        initializationInFlight.current = false;
      }
    };

    initializeChat();
  }, [dispatch, isConnected, isConnecting, isLoggedIn, buyer, error]);

  // Handle logout - disconnect chat
  useEffect(() => {
    if (!isLoggedIn && isConnected) {
      dispatch(setChatDisconnected());
      dispatch(setUnreadCount(0));
    }
  }, [dispatch, isConnected, isLoggedIn]);

  // This component doesn't render anything
  return null;
};
